import { useState } from 'react';
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { ScanLine, Search } from 'lucide-react';

import { BarcodeScannerDialog } from '@/components/quotes/BarcodeScannerDialog';
import { PlanLockBadge } from '@/components/quotes/PlanLockBadge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { useQuoteEntitlement } from '@/hooks/useQuoteEntitlement';
import { showApiError } from '@/lib/apiErrors';

const API_URL = process.env.REACT_APP_API_URL;
const WARRANTY_CLASS = { expired: 'text-red-600', ending: 'text-amber-700', active: 'text-emerald-700' };

/** Find the installed device, customer and warranty for a serial number. */
export function SerialSearch({ fmtDate, onOpenCustomer }) {
  const { t } = useTranslation('quotes');
  const { entitlement } = useQuoteEntitlement();
  const locked = entitlement?.limits?.barcode_scanning === false;
  const [term, setTerm] = useState('');
  const [results, setResults] = useState(null);
  const [scanning, setScanning] = useState(false);

  const search = async (value) => {
    const q = value.trim();
    if (q.length < 3) {
      toast.error(t('service.serial.tooShort'));
      return;
    }
    try {
      const { data } = await axios.get(`${API_URL}/api/quote/service/devices/by-serial`, { params: { q } });
      setResults(data.items || []);
    } catch (err) {
      showApiError(err, t);
    }
  };

  return (
    <div className="mb-6">
      <form className="flex gap-2" onSubmit={(e) => { e.preventDefault(); search(term); }}>
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-[#94A3B8]" />
          <Input
            value={term}
            onChange={(e) => { setTerm(e.target.value); setResults(null); }}
            placeholder={t('service.serial.searchPlaceholder')}
            className="pl-9"
            data-testid="serial-search-input"
          />
        </div>
        <Button
          type="button"
          variant="outline"
          onClick={() => (locked ? window.location.assign('/quote-upgrade') : setScanning(true))}
          aria-label={t('service.serial.scan')}
          data-testid="serial-search-scan"
        >
          <ScanLine className="h-4 w-4" />
          {locked && <PlanLockBadge />}
        </Button>
      </form>

      {results && (
        <div className="mt-2 overflow-hidden rounded-2xl border border-[#E5E5E5] bg-white" data-testid="serial-search-results">
          {results.length === 0 ? (
            <div className="p-6 text-center text-sm text-[#94A3B8]">{t('service.serial.noResult')}</div>
          ) : (
            <ul className="divide-y divide-[#F1F5F9]">
              {results.map((d) => (
                <li key={d.id}>
                  <button type="button" onClick={() => onOpenCustomer(d.customer_id)} className="w-full px-4 py-3 text-left hover:bg-slate-50">
                    <div className="truncate font-medium text-[#1A1A2E]">{d.customer_name}</div>
                    <div className="truncate text-sm text-[#475569]">{d.name}{d.location ? ` · ${d.location}` : ''}</div>
                    <div className="truncate text-xs text-[#94A3B8]">
                      S/N: {d.matched_serial} · {t('service.device.installedShort', { date: fmtDate(d.installed_at) })}
                    </div>
                    {WARRANTY_CLASS[d.warranty_state] && (
                      <div className={`text-xs ${WARRANTY_CLASS[d.warranty_state]}`}>
                        {t(`service.device.warranty.${d.warranty_state}`, { date: fmtDate(d.warranty_until) })}
                      </div>
                    )}
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}

      <BarcodeScannerDialog
        open={scanning}
        mode="serial"
        title={t('service.serial.scan')}
        help={t('service.serial.searchHelp')}
        onClose={() => setScanning(false)}
        onDetected={(code) => { setScanning(false); setTerm(code); search(code); }}
      />
    </div>
  );
}
